import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Brain,
  Sparkles,
  ArrowRight,
  Replace,
  IndianRupee,
  Flag,
  Camera,
  ShieldCheck,
  HelpCircle,
  AlertTriangle,
} from "lucide-react";
import { cls } from "../../lib/cls";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { mergeProjectLayers } from "../../lib/projectLayers";
import { askAgent } from "../../lib/agent/agentClient";
import type { GraphNode } from "../../types/ontology";
import { CHAT_PROMPTS } from "../../data/generated/chat-prompts.generated";

// Icon-name → lucide component resolver (YAML stores names; React needs nodes).
const ICONS: Record<string, JSX.Element> = {
  Send: <Send className="w-3 h-3" />,
  Brain: <Brain className="w-3 h-3" />,
  Sparkles: <Sparkles className="w-3 h-3" />,
  ArrowRight: <ArrowRight className="w-3 h-3" />,
  Replace: <Replace className="w-3 h-3" />,
  IndianRupee: <IndianRupee className="w-3 h-3" />,
  Flag: <Flag className="w-3 h-3" />,
  Camera: <Camera className="w-3 h-3" />,
  ShieldCheck: <ShieldCheck className="w-3 h-3" />,
};
function iconFor(name: string): JSX.Element { return ICONS[name] ?? <Sparkles className="w-3 h-3" />; }

// DesignerChat is graph-powered via LightRAG over the project's typed
// hypergraph — supervisor + 10 skills, retrieval over typed
// :Room/:SKU/:Approval/:Milestone/:MemoryCard nodes via sparql_grounding
// + memory_recall (bge-m3 + Tantivy). NO keyword templates. CLAUDE.md §3
// (one gateway) + §5 (no silent catches) + memory feedback_react_lightrag_agents.md.

interface CitedNode { id: string; label: string; kind: string; }
interface AgentReply {
  text: string;
  cited?: CitedNode[];
  refusal?: boolean;
  /** True when the cluster's agent endpoint isn't bound (AGENT_BASE_URL
   *  not set in CF Pages env). Surfaces the honest "agent brain not bound"
   *  message instead of fabricating an answer. */
  unbound?: boolean;
  actorId?: string;
}
interface Message { id: string; role: "user" | "agent"; text: string; reply?: AgentReply; ts: string; }

interface DesignerChatProps { onNavigateToNode?: ((id: string) => void) | undefined; }

export function DesignerChat({ onNavigateToNode }: DesignerChatProps): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  // Re-merge on project change / placement edit so the cited chips
  // resolve against the live graph.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:timeline", bump);
    window.addEventListener("foyrmind:projectChanged", bump);
    return () => {
      window.removeEventListener("foyrmind:timeline", bump);
      window.removeEventListener("foyrmind:projectChanged", bump);
    };
  }, []);
  const graph = useMemo(() => { void tick; return mergeProjectLayers(baseGraph, activeId); }, [baseGraph, activeId, tick]);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "m0",
      role: "agent",
      text: "Ask about any room, item, milestone, vendor or approval on this project — answers come from LightRAG over the typed hypergraph. Click any evidence chip to jump to that node.",
      ts: nowTs(),
    },
  ]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Node lookup for cited chips: turn "ikea-pax-wardrobe" returned by
  // the supervisor into a CitedNode with kind + label. Citations that
  // don't match a graph node are rendered as text-only chips.
  const nodeIndex = useMemo<Map<string, GraphNode>>(
    () => new Map(graph.nodes.map((n) => [n.id, n])),
    [graph.nodes],
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  async function send(text?: string): Promise<void> {
    const q = (text ?? draft).trim();
    if (!q) return;
    const userMsg: Message = { id: `m-${Date.now()}-u`, role: "user", text: q, ts: nowTs() };
    setMessages((cur) => [...cur, userMsg]);
    setDraft("");
    setThinking(true);

    // Conversation history (compact, last 12 turns) for the agent prompt.
    const history = messages
      .slice(-12)
      .map((m) => ({ role: m.role, text: m.text }));

    const result = await askAgent({
      projectId: activeId,
      question: q,
      history,
      defaultAgentId: "supervisor",
      timelineSnapshot: [],
    });
    setThinking(false);

    if (!result.ok) {
      const unbound = result.status === 503 || result.status === 404 || result.status === 0;
      const text503 = `Agent brain not bound yet (HTTP ${result.status}). ${result.detail}`;
      const text500 = `Agent endpoint error: ${result.detail}`;
      setMessages((cur) => [...cur, {
        id: `m-${Date.now()}-a`, role: "agent",
        text: unbound ? text503 : text500,
        reply: { text: unbound ? text503 : text500, unbound, refusal: false, actorId: "supervisor" },
        ts: nowTs(),
      }]);
      return;
    }

    // Map the supervisor's string-array `cited` into CitedNode chips
    // where the ids resolve to real graph nodes. Strings that don't
    // resolve render as text-only chips (still useful — slugs like
    // "internal/sku-collision" carry meaning even without a node).
    const citedChips: CitedNode[] = result.cited.map((c) => {
      const node = nodeIndex.get(c);
      if (node) return { id: node.id, label: node.label, kind: node.kind };
      return { id: c, label: c, kind: "Citation" };
    });

    setMessages((cur) => [...cur, {
      id: `m-${Date.now()}-a`, role: "agent",
      text: result.text,
      reply: { text: result.text, cited: citedChips, actorId: result.actorId },
      ts: nowTs(),
    }]);
  }

  return (
    <div className="panel p-0 overflow-hidden flex flex-col h-[640px]">
      <header className="px-4 py-3 border-b border-border flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg bg-ink-950 text-cream grid place-items-center">
          <Brain className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink-950 leading-tight">Designer copilot</div>
          <div className="text-[11px] text-ink-500 leading-tight">LightRAG over this project's typed hypergraph</div>
        </div>
        <span className="ml-auto pill bg-fern/10 text-fern">
          <Sparkles className="w-3 h-3" /> graph-powered
        </span>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-cream-deeper/30">
        <AnimatePresence initial={false}>
          {messages.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={cls("flex", m.role === "user" ? "justify-end" : "justify-start")}
            >
              <div
                className={cls(
                  "max-w-[92%] rounded-2xl px-3 py-2.5 text-[12px] leading-snug",
                  m.role === "user"
                    ? "bg-ink-950 text-cream rounded-tr-md"
                    : m.reply?.unbound
                      ? "bg-foyr-deep/5 border border-foyr-deep/30 text-ink-950 rounded-tl-md"
                      : "bg-white border border-border text-ink-950 rounded-tl-md",
                )}
              >
                <div className="whitespace-pre-wrap">{m.text}</div>
                {m.reply ? <AgentReplyBlock reply={m.reply} onNavigate={onNavigateToNode} /> : null}
              </div>
            </motion.div>
          ))}
          {thinking ? (
            <motion.div key="thinking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex justify-start">
              <div className="bg-white border border-border rounded-2xl rounded-tl-md px-3 py-2 text-[11px] text-ink-500 inline-flex items-center gap-1.5">
                <ThinkingDot /><ThinkingDot delay="120ms" /><ThinkingDot delay="240ms" />
                <span className="ml-1 italic">retrieving over the typed graph…</span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <div className="px-3 py-2 border-t border-border bg-cream">
        <div className="flex gap-1.5 flex-wrap mb-2">
          {/* Suggested prompts now PRE-FILL the input and trigger the same
              LightRAG path — they no longer expand a canned template. */}
          {CHAT_PROMPTS.slice(0, 6).map((p) => (
            <button
              key={p.id}
              onClick={() => { void send(p.q); }}
              className="pill bg-cream-dark border border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950 transition"
            >
              {iconFor(p.icon)}
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(ev) => setDraft(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); void send(); } }}
            placeholder="Ask about a SKU, room, vendor, milestone, budget line, approval…"
            className="flex-1 text-[13px] bg-white border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-foyr/30"
          />
          <button onClick={() => { void send(); }} className="btn btn-foyr !px-3 !py-2" disabled={!draft.trim() || thinking}>
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Reply rendering ─── */

function AgentReplyBlock({ reply, onNavigate }: { reply: AgentReply; onNavigate: ((id: string) => void) | undefined }): JSX.Element {
  if (reply.refusal) {
    return (
      <div className="mt-2 text-[11px] text-ink-500 flex items-center gap-1">
        <HelpCircle className="w-3 h-3" /> only this project · no made-up answers
      </div>
    );
  }
  return (
    <div className="mt-2.5 space-y-2.5">
      {reply.unbound ? (
        <div className="text-[10.5px] text-foyr-deep flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> agent/not-bound
        </div>
      ) : null}
      {reply.cited && reply.cited.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-accent mb-1">Evidence</div>
          <div className="flex flex-wrap gap-1.5">
            {reply.cited.map((c) => {
              const clickable = c.kind !== "Citation";
              return (
                <button
                  key={c.id}
                  onClick={() => { if (clickable) onNavigate?.(c.id); }}
                  className={cls(
                    "pill bg-cream-dark border border-border text-ink-700 transition",
                    clickable ? "hover:border-foyr/40 hover:text-ink-950 cursor-pointer" : "cursor-default opacity-80",
                  )}
                >
                  <span className="text-[9px] font-mono text-ink-500">{c.kind}</span>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ThinkingDot({ delay = "0ms" }: { delay?: string }): JSX.Element {
  return <span className="w-1.5 h-1.5 rounded-full bg-foyr animate-pulse" style={{ animationDelay: delay }} />;
}

function nowTs(): string {
  const d = new Date();
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}
